<script setup lang="ts">
const emit = defineEmits(["sortType", "updateMerchant"]);
const props = defineProps({
  itemNumber: {
    type: Number,
    required: false,
    default: 0,
  },
  altTotal: {
    type: Number,
    required: false,
  },
  isCollectionView: {
    type: Boolean,
    required: false,
    default: false,
  },
  showFilters: {
    type: Boolean,
    required: false,
    default: false,
  },
  showBackButton: {
    type: Boolean,
    required: false,
    default: false,
  },
  backPath: {
    type: String,
    required: false,
    default: "/store",
  },
});

const { t } = useI18n();
const route = useRoute();
const merchant = computed(() => route.params.merchant as string | undefined);
const sortType = ref("default");
const showListed = defineModel("showListed", {
  type: Boolean,
  default: true,
});

const resultsText = computed(() => {
  if (typeof props.altTotal !== "number") return "";
  return showListed.value
    ? t("main.toolbar.listed", props.itemNumber)
    : t("main.toolbar.unlisted", props.itemNumber);
});

const hasTotals = computed(() => typeof props.altTotal === "number");

const countClasses = computed(() =>
  hasTotals.value
    ? ["toolbar__count"]
    : ["toolbar__count", "toolbar__count--muted"]
);

const totalClasses = computed(() => {
  const classes = ["toolbar__toggle"];

  if (props.altTotal === 0) {
    classes.push("toolbar__toggle--disabled");
  }

  return classes;
});

watch(sortType, () => {
  emit("sortType", sortType.value);
});

const updateMerchant = (newMerchant: string) => {
  emit("updateMerchant", newMerchant);
};

const toggleListed = () => {
  if (props.altTotal === 0) return;
  showListed.value = !showListed.value;
};
</script>
<template>
  <div
    class="sm:px-1 my-4 flex flex-wrap items-center gap-x-4 md:gap-x-6 gap-y-4 w-full min-h-[48px]">
    <NuxtLinkLocale
      v-if="showBackButton"
      :to="backPath"
      class="flex items-center cursor-pointer text-neutral-300 hover:text-primary-500">
      <NuxtIcon name="mdi:chevron-left" class="w-6 h-6" />
      <p class="text-xl font-bold">{{ $t("main.storeDetail.backToList") }}</p>
    </NuxtLinkLocale>
    <div
      class="w-full sm:w-auto flex justify-between sm:justify-normal items-center gap-4 flex-wrap">
      <CommonGameSelector
        v-if="isCollectionView && !merchant"
        :item-number="itemNumber"
        @update-merchant="updateMerchant"
        class="order-1 sm:order-none" />
      <div
        v-if="
          typeof itemNumber === 'number' && typeof props.altTotal === 'number'
        "
        class="flex items-baseline gap-x-2">
        <p
          class="text-base sm:text-xl font-bold min-w-fit"
          :class="countClasses">
          {{ itemNumber }}
          {{ resultsText }}
        </p>
        <span class="text-neutral-300 font-bold">/</span>
        <p
          @click="toggleListed"
          class="text-neutral-300 text-sm sm:text-base font-bold min-w-fit"
          :class="totalClasses">
          {{ altTotal }}
          {{
            !showListed
              ? $t("main.toolbar.listed", altTotal || 0)
              : $t("main.toolbar.unlisted", altTotal || 0)
          }}
        </p>
      </div>
    </div>
    <ToolbarFilters v-if="showFilters" />
  </div>
</template>
